import { Component, inject, OnDestroy, signal } from '@angular/core';
import { ADDIN_CORE_CONFIG, SettingsEntry } from '../../config/addin-core-config';
import { ConfigService } from '../../config/config.service';
import { TranslatePipe } from '../../i18n/translate.pipe';
import { SettingsLink } from '../settings-link/settings-link';

/** Shared entries, listed after the host specific ones from {@link ADDIN_CORE_CONFIG}. */
const CORE_SETTINGS_ENTRIES: SettingsEntry[] = [
  {
    route: '/settings/lang',
    titleKey: 'settings.language',
    subtitleKey: 'settings.languageSubtitle',
    iconPath:
      'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
  },
  {
    route: '/tenants',
    titleKey: 'settings.tenants',
    subtitleKey: 'settings.tenantsSubtitle',
    iconPath:
      'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
  }
];

@Component({
  selector: 'cmi-settings',
  imports: [SettingsLink, TranslatePipe],
  templateUrl: './settings.html'
})
export class Settings implements OnDestroy {
  private readonly _configService = inject(ConfigService);
  private _copyFeedbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

  protected readonly entries = [
    ...inject(ADDIN_CORE_CONFIG).settingsEntries,
    ...CORE_SETTINGS_ENTRIES
  ];
  protected readonly buildVersion = this._configService.config.buildVersion;
  protected readonly copyFeedback = signal<'copied' | 'failed' | null>(null);

  protected copyBuildVersion(): void {
    if (!this.buildVersion) {
      return;
    }

    void this._copyToClipboard(this.buildVersion);
  }

  private async _copyToClipboard(value: string): Promise<void> {
    const didCopyWithNavigator = await this._copyWithNavigatorClipboard(value);
    const didCopy = didCopyWithNavigator || this._copyWithExecCommand(value);

    this.copyFeedback.set(didCopy ? 'copied' : 'failed');
    this._scheduleCopyFeedbackReset();
  }

  private async _copyWithNavigatorClipboard(value: string): Promise<boolean> {
    const clipboard = globalThis.navigator?.clipboard;
    if (!clipboard?.writeText) {
      return false;
    }

    try {
      await clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  }

  private _copyWithExecCommand(value: string): boolean {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    let didCopy = false;
    try {
      didCopy = document.execCommand('copy');
    } catch {
      didCopy = false;
    }

    document.body.removeChild(textarea);
    return didCopy;
  }

  private _scheduleCopyFeedbackReset(): void {
    if (this._copyFeedbackTimeoutId !== null) {
      clearTimeout(this._copyFeedbackTimeoutId);
    }

    this._copyFeedbackTimeoutId = setTimeout(() => {
      this.copyFeedback.set(null);
      this._copyFeedbackTimeoutId = null;
    }, 2000);
  }

  public ngOnDestroy(): void {
    if (this._copyFeedbackTimeoutId !== null) {
      clearTimeout(this._copyFeedbackTimeoutId);
      this._copyFeedbackTimeoutId = null;
    }
  }
}
